// ─── Multi-tenant subdomains ────────────────────────────────────────────────
// Single source of truth for both the signup validator (api) and the live
// availability check (admin) — see plan.md §3.1. Add new reserved words here
// only, never duplicate the list at either call site.
export const RESERVED_SUBDOMAINS: readonly string[] = [
  'www', 'api', 'admin', 'account', 'chat', 'mail', 'ftp', 'cpanel', 'app', 'cdn',
  'assets', 'status', 'docs', 'blog', 'support', 'help', 'billing', 'staging', 'test', 'dev',
  'sp-web', 'sp-admin', 'sp-api', 'sp-account', 'sp-chat-widget', 'sp-chatservice',
];

export type SubdomainInvalidReason = 'reserved' | 'taken' | 'invalid-format';

export interface SubdomainAvailability {
  available: boolean;
  reason?: SubdomainInvalidReason;
}

/** Returned by GET /store/info — the storefront's boot-time tenant check. */
export interface StoreInfoResponse {
  id: string;
  name: string;
  currency: string;
}

/**
 * Standard DNS label rules: lowercase alphanumeric + hyphen, 3-63 chars, no
 * leading/trailing hyphen. Returns the format error, or null if the format
 * (ignoring reservation/uniqueness, checked separately) is valid.
 */
export function validateSubdomainFormat(value: string): string | null {
  const v = value.trim().toLowerCase();
  if (v.length < 3 || v.length > 63) return 'Must be 3-63 characters';
  if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(v)) {
    return 'Lowercase letters, numbers, and hyphens only — no leading or trailing hyphen';
  }
  return null;
}

// 'predefined' reads its GST rates off taxProfileId; 'custom' reads the
// product's own custom*RateBps fields instead. See checkout.service.ts's
// calculateTotals() for how these resolve into an actual CGST/SGST/IGST
// split at checkout time.
export type TaxMode = 'predefined' | 'custom';

export interface ProductTaxFields {
  taxMode?: TaxMode;
  taxProfileId?: string;
  /** Joined display name of taxProfileId — read-only, ignored on write. */
  taxProfileName?: string;
  hsnCode?: string;
  customIgstRateBps?: number;
  customCgstRateBps?: number;
  customSgstRateBps?: number;
  isTaxInclusive?: boolean;
}

export interface ProductSummary extends ProductTaxFields {
  /**
   * Set only when a create fanned out across several stores: one product row
   * was made per store and this is how many. Absent for a single-store create.
   */
  createdInStores?: number;
  id: string;
  name: string;
  slug: string;
  category: string;
  categoryId?: string;
  price: number;
  discount?: number;
  paymentMethods?: string[];
  imageUrl: string;
  mediaSizeBytes: number;
  stock: number;
  active: boolean;
  createdAt: string;
  updatedAt?: string;
  sku?: string;
  barcode?: string;
  brandId?: string;
  /** Joined display name of brandId — read-only, ignored on write. */
  brandName?: string;
  variantCount?: number;
  averageRating?: number;
  reviewCount?: number;
  /** Size the product is sold in, stored in base units — see formatUnit(). */
  unitOfMeasure?: string | null;
  unitQuantity?: number | null;
}

export interface ProductImageSummary {
  url: string;
  altText: string;
}

/**
 * Kept as an alias so existing imports keep working. The tiers themselves are
 * defined once in @cart/shared-utils; this used to read
 * `'free' | 'standard' | 'premium'`, and 'standard' is a tier no migration ever
 * produced — the API has always written 'basic'.
 */
export type PlanTier = MerchantPlan;

export interface OrgImageUsage {
  usedBytes: number;
  capBytes: number;
  planTier: PlanTier;
  /** Uploads are refused: past the block point, or suspended by an admin. */
  isHardBlocked: boolean;
  /** Past the plan allowance but still inside the grace band — shown in red. */
  isOverLimit?: boolean;
  /** The byte count at which uploads stop. Above capBytes; see the quota service. */
  blockAtBytes?: number;
  /** Premium but unverified, so running on the provisional allowance. */
  awaitingPlanVerification?: boolean;
  availableBytes?: number;
  usagePercent?: number;
  /** Bytes held by deleted files awaiting purge — the usual reason someone is stuck. */
  trashedBytes?: number;
}

export interface TrashedProductImage {
  id: string;
  productId: string;
  productName: string;
  url: string;
  altText: string;
  sizeBytes: number;
  trashedAt: string;
}

export interface ProductDetail extends ProductSummary {
  description: string;
  images: ProductImageSummary[];
  specs?: ProductSpec[];
  /** Sizes this product is sold in. Empty for a single-size product. */
  variants?: ProductVariantSummary[];
}

/**
 * One purchasable size of a product — "500 ml", "pack of 6".
 *
 * Price is per variant because that is the whole point: a litre is not twice
 * the price of 500 ml often enough to derive it.
 */
export interface ProductVariantSummary {
  id: string;
  title: string;
  sku?: string | null;
  price: number;
  compareAtPrice?: number | null;
  unitOfMeasure?: string | null;
  unitQuantity?: number | null;
  /**
   * Option values this variant stands for, e.g. `{ "Size": "L" }` or
   * `{ "RAM": "8GB", "Storage": "512GB" }`.
   *
   * Not every variant is a measurable size: clothing sells by S/M/L, phones by
   * RAM + storage, a thali by quarter/half/full. Those are *options*, and a
   * unit column cannot express them — which is why both live side by side.
   */
  options?: Record<string, string> | null;
  /**
   * Units of this variant a shopper can still buy.
   *
   * Per variant, not per product: a store holding six 500 ml and no 1 L must
   * not offer the 1 L. Omitted when the product is not stocked by variant.
   */
  stock?: number;
  isDefault: boolean;
}

export type ProductStatusFilter = 'active' | 'draft' | 'out_of_stock' | 'low_stock';
export type ProductSort =
  | 'price-asc' | 'price-desc' | 'newest'
  | 'name-asc' | 'name-desc'
  | 'sku-asc' | 'sku-desc'
  | 'category-asc' | 'category-desc'
  | 'updated-asc' | 'updated-desc';

export interface ProductListQuery {
  search?: string;
  /** Single-slug filter — kept for the public storefront controller. */
  category?: string;
  /** Multi-select by id — the admin category tree filter. */
  categoryIds?: string[];
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  /** Same 4 buckets the admin status pills/6-card stat row use. */
  status?: ProductStatusFilter;
  taxMode?: TaxMode;
  taxProfileId?: string;
  /** Matches products on a predefined profile at this rate, or custom-mode
   *  products with this exact custom IGST rate. 0 doubles as "No Tax". */
  taxRateBps?: number;
  /** 'missing' = predefined mode with no profile assigned. */
  taxConfigStatus?: 'configured' | 'missing';
  brandId?: string;
  sort?: ProductSort;
  page?: number;
  limit?: number;
  includeInactive?: boolean;
}

export interface ProductListResponse {
  items: ProductSummary[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CategoryCountsResponse {
  /** categoryId -> number of active products directly in that category. */
  counts: Record<string, number>;
}

export interface ProductStatsResponse {
  total: number;
  active: number;
  draft: number;
  outOfStock: number;
  lowStock: number;
  /** No archive state exists in the data model yet — always 0. */
  archived: number;
}

export interface ProductSpec {
  key: string;
  value: string;
}

export interface CreateProductRequest extends ProductTaxFields {
  /**
   * Stores to create this product in. Omitted or empty means the store the
   * request is scoped to, which is what every existing caller does.
   *
   * Each store gets its **own** product row — see ProductsService.create().
   */
  storeIds?: string[];
  name: string;
  slug: string;
  category: string;
  categoryId?: string;
  price: number;
  discount?: number;
  paymentMethods?: string[];
  stock: number;
  imageUrl?: string;
  description?: string;
  specs?: ProductSpec[];
  active?: boolean;
  sku?: string;
  barcode?: string;
  unitOfMeasure?: string | null;
  unitQuantity?: number | null;
}

export interface UpdateProductRequest extends ProductTaxFields {
  name?: string;
  slug?: string;
  description?: string;
  specs?: ProductSpec[];
  price?: number;
  discount?: number;
  active?: boolean;
  categorySlug?: string;
  categoryId?: string;
  paymentMethods?: string[];
  sku?: string;
  barcode?: string;
  unitOfMeasure?: string | null;
  unitQuantity?: number | null;
}

export interface UpdateInventoryRequest {
  stock: number;
}

export interface CategorySummary {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  level: number;
  /**
   * Products a shopper can actually browse — active only.
   *
   * The storefront prints this next to the category name, so counting drafts
   * promised items that were not there.
   */
  productCount: number;
  /**
   * Every product filed under the category, drafts included.
   *
   * Only meaningful to a merchant: the delete dialog says how many products
   * stay in the catalogue after the category goes, and a draft is just as
   * affected as a live one.
   */
  totalProductCount: number;
  createdAt: string;
}

export interface CategoryListResponse {
  items: CategorySummary[];
  total: number;
}

export interface CreateCategoryRequest {
  name: string;
  slug: string;
  parentId?: string | null;
  level?: number;
}

export interface UpdateCategoryRequest {
  name?: string;
  parentId?: string | null;
  level?: number;
}

export interface CartItemSummary {
  id: string;
  productId: string;
  productName: string;
  productSlug: string;
  productImageUrl?: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  stock: number;
  /** Set when the shopper picked a specific size/option of the product. */
  variantId?: string | null;
  /** Human label for that choice — "1 L", "L", "8GB + 512GB". */
  variantLabel?: string | null;
}

export interface CartSummary {
  id: string;
  items: CartItemSummary[];
  subtotal: number;
  totalItems: number;
  couponCode?: string;
  discountTotal: number;
  total: number;
}

export interface ApplyCouponRequest {
  code: string;
}

export interface AddCartItemRequest {
  productId: string;
  quantity: number;
  /**
   * Which size/option to buy. Optional because plenty of products have only
   * one version; when given, the line is priced from the variant, not the
   * product, since a litre is rarely twice the price of 500 ml.
   */
  variantId?: string | null;
}

export interface UpdateCartItemRequest {
  quantity: number;
}

export interface CheckoutAddress {
  recipientName: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone?: string;
}

export interface CreateCheckoutSessionRequest {
  email?: string;
  shippingAddress?: CheckoutAddress;
  billingAddress?: CheckoutAddress;
  /**
   * @deprecated Ignored. Shipping is priced server-side from the delivery
   * address; a rate id chosen by the client used to set the price directly,
   * which let a shopper pay local rates for a far-flung address.
   */
  shippingRateId?: string;
  /**
   * Collecting from the shop instead of having it delivered.
   *
   * The only delivery choice a client may make, and it carries no price — the
   * server zeroes shipping for a pickup rather than trusting a figure.
   */
  pickup?: boolean;
  discountCode?: string;
}

export interface UpdateCheckoutSessionRequest
  extends CreateCheckoutSessionRequest {
  status?: 'open' | 'completed' | 'expired' | 'cancelled';
}

export interface CompleteCheckoutSessionRequest {
  paymentProvider?: string;
  paymentReference?: string;
  paymentStatus?: 'paid' | 'pending';
}

// GST snapshot for one line — computed once at checkout time (see
// checkout.service.ts's calculateTotals()) and frozen onto the line, so
// later edits to a tax slab profile never retroactively change an
// already-priced cart/order line.
export interface GstLineBreakdown {
  hsnCode?: string;
  igstRateBps?: number;
  cgstRateBps?: number;
  sgstRateBps?: number;
  isInterState?: boolean;
  taxableValue?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
}

// Aggregate GST split — cgstTotal + sgstTotal + igstTotal == taxTotal.
// Customer-facing (shown at checkout/on orders). tcsTotal is a SEPARATE,
// admin-only accounting figure — real Indian GST TCS is withheld from a
// seller's payout, it never adds to what the customer pays, so it must
// only ever be populated on admin-facing responses, never the
// customer-facing checkout/order read paths.
export interface GstTotals {
  cgstTotal?: number;
  sgstTotal?: number;
  igstTotal?: number;
  /** Admin-only — see the note above. Must stay unset on customer-facing responses. */
  tcsTotal?: number;
}

export interface CheckoutLineItemSummary extends GstLineBreakdown {
  id: string;
  productId: string;
  variantId?: string;
  title: string;
  sku?: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

export interface CheckoutSessionSummary extends GstTotals {
  id: string;
  token: string;
  status: string;
  email?: string;
  shippingAddress?: CheckoutAddress;
  billingAddress?: CheckoutAddress;
  items: CheckoutLineItemSummary[];
  subtotal: number;
  taxTotal: number;
  shippingTotal: number;
  discountTotal: number;
  grandTotal: number;
  currency: string;
  expiresOn?: string;
  createdAt: string;
}

export type OrderStatus =
  | 'placed'
  | 'processing'
  | 'shipped'
  | 'out_for_delivery'
  | 'delivered'
  | 'cancelled';

export interface OrderItemSummary extends GstLineBreakdown {
  id: string;
  productId: string;
  productName: string;
  productSlug: string;
  productImageUrl?: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  /** The size/option bought, when the product is sold in more than one. */
  variantId?: string | null;
  /** Label for that choice — "1 L", "L", "8GB + 512GB". */
  variantLabel?: string | null;
}

export interface OrderStatusHistoryEntry {
  fromStatus?: string;
  toStatus: string;
  createdAt: string;
}

export interface OrderSummary extends GstTotals {
  id: string;
  status: OrderStatus;
  items: OrderItemSummary[];
  subtotal: number;
  taxTotal?: number;
  shippingTotal?: number;
  discountTotal?: number;
  grandTotal?: number;
  paymentStatus?: string;
  fulfillmentStatus?: string;
  totalItems: number;
  createdAt: string;
  orderNumber?: string;
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  shippingAddress?: CheckoutAddress;
  statusHistory?: OrderStatusHistoryEntry[];
}

export interface OrderListResponse {
  items: OrderSummary[];
  total: number;
}

export interface UpdateOrderStatusRequest {
  status: OrderStatus;
}

export type UserRole =
  | 'customer'
  | 'admin'
  // Platform roles (your team managing the Shopeee platform)
  | 'platform_admin'
  | 'account_manager'
  | 'operator'
  // Merchant roles (business owners using the platform)
  | 'merchant'
  | 'store_manager'
  | 'accountant'
  // Legacy — kept for DB backward compat
  | 'super_admin'
  | 'store_owner';

export const PLATFORM_ROLES: UserRole[] = ['platform_admin', 'account_manager', 'operator'];
export const MERCHANT_ROLES: UserRole[] = ['merchant', 'store_manager', 'accountant'];

export type ThemeMode = 'light' | 'dark';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  permissions?: string[];
  storeId?: string;
  merchantId?: string;
  themeMode: ThemeMode;
  createdAt: string;
  // Undefined = not verified. Pre-existing accounts were grandfathered in
  // (see migration 018) — this only ever gates accounts registered after
  // email verification shipped.
  emailVerifiedAt?: string;
  // Populated only for role === 'merchant' (via Organization.approvalStatus
  // through the user's organizationId) — lets the frontend route a freshly
  // logged-in merchant straight to /application-status/:organizationId
  // without an extra call.
  organizationId?: string;
  organizationName?: string;
  organizationApprovalStatus?: MerchantApprovalStatus;
  organizationRejectionReason?: string;
  /**
   * Suspended merchants CAN still sign in — this drives the warning banner
   * rather than blocking access. Deleted merchants never get this far: the
   * login itself is refused, so there is no `organizationDeleted` counterpart.
   */
  organizationSuspended?: boolean;
  organizationSuspensionReason?: string;
}

export interface UserListResponse {
  items: AuthUser[];
  total: number;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  // Same purpose as ForgotPasswordRequest.returnOrigin — lets the
  // verification email link back to whichever frontend origin the request
  // actually came from (dev port, staging, prod).
  returnOrigin?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
  returnOrigin: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

// register() no longer returns a usable session — the account can't log in
// until it's verified, so there's nothing an accessToken would be for yet.
export interface RegisterResponse {
  email: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
  returnOrigin: string;
}

// Authenticated — backs the sp-account.forgestack.in "which store" picker
// shown after a global login. One identity (User) can have a
// CustomerStoreMembership at more than one store; this lists them, grouped
// by merchant (organizationId/organizationName) in the picker UI. Sorted by
// most-recently-used first, so the picker's default/highlighted store is
// just stores[0] when nothing is already selected client-side.
export interface MyStoresResponse {
  stores: { name: string; domain: string; role: string; organizationId: string; organizationName: string }[];
}

// ─── Merchant registration + admin approval ─────────────────────────────
export type MerchantApprovalStatus = 'pending' | 'approved' | 'rejected';
export type MerchantPlan = 'free' | 'basic' | 'premium';

// Public, no-auth lookup (GET /auth/application-status/:id) backing the
// shareable /application-status/:id admin page — a merchant's own status
// link works whether or not their session is still active. Deliberately
// excludes anything sensitive (email, phone, gstin) since it needs no
// authentication to view; the id itself (Organization.id, a uuid) is the
// only "secret".
export interface ApplicationStatusResponse {
  organizationId: string;
  businessName: string;
  approvalStatus: MerchantApprovalStatus;
  rejectionReason?: string;
  plan: MerchantPlan;
}

export const MERCHANT_BUSINESS_TYPES: readonly string[] = [
  'Retail', 'Fashion', 'Electronics', 'Food & Beverage', 'Home & Living', 'Other',
];

export interface RegisterMerchantRequest {
  email: string;
  password: string;
  name: string;
  businessName: string;
  businessType: string;
  phone: string;
  plan: MerchantPlan;
  gstin?: string;
  city?: string;
  state?: string;
  returnOrigin?: string;
}

// Same "can't log in yet" shape as RegisterResponse — verification is
// still required before the account can log in, auto-approved or not.
export interface RegisterMerchantResponse {
  email: string;
}

// Live email-availability check, queried on blur of the email field before
// the form is even submitted — lets the register-merchant page offer a
// "resend verification" link immediately instead of only surfacing the
// same information as a 409 after the whole form is filled in and submitted.
// 'available': no account with this email (for this role) — free to use.
// 'unverified': an account already exists for this role but never verified
//   — registerMerchant()/register() will resend the link rather than block.
// 'taken': a verified account exists, or one under a different role —
//   submitting will be rejected with a genuine conflict.
export type EmailAvailabilityStatus = 'available' | 'unverified' | 'taken';

export interface CheckEmailStatusResponse {
  status: EmailAvailabilityStatus;
}

export interface ResubmitMerchantRegistrationRequest {
  businessName?: string;
  businessType?: string;
  phone?: string;
  gstin?: string;
  city?: string;
  state?: string;
}

export interface ApproveMerchantRequest {
  maxStores: number;
  storageCapBytes?: number;
  maxImageSizeBytes?: number;
  maxImagesPerProduct?: number;
}

/**
 * Quotas and plan, editable *after* approval.
 *
 * Same shape as `ApproveMerchantRequest` plus the plan fields, so an operator
 * sees one consistent form whether they are approving a new merchant or
 * adjusting an existing one. Every field is optional: this is a patch, and
 * omitting a field must leave it alone rather than reset it.
 */
export interface UpdateMerchantSettingsRequest {
  /**
   * Confirms this account is entitled to the plan it selected.
   *
   * Until payments exist a paid plan is self-selected, so an unverified basic
   * or premium account runs on a provisional storage allowance. Setting this
   * releases the plan's real allowance; clearing it puts the account back on
   * the provisional one.
   */
  planVerified?: boolean;
  plan?: MerchantPlan;
  planExpiresAt?: string | null;
  maxStores?: number | null;
  storageCapBytes?: number | null;
  maxImageSizeBytes?: number | null;
  maxImagesPerProduct?: number | null;
}

/** Which slice of the merchant list to show. `deleted` is its own view. */
export type MerchantListFilter = 'all' | 'active' | 'suspended' | 'pending' | 'deleted';

/**
 * Sortable columns.
 *
 * `storeCount` is an aggregate over the stores relation, not a column — see
 * `PlatformService.listMerchants`.
 */
export type MerchantListSort =
  | 'createdAt'
  | 'name'
  | 'email'
  | 'plan'
  | 'approvalStatus'
  | 'storeCount';

export const MERCHANT_LIST_SORTS: readonly MerchantListSort[] = [
  'createdAt', 'name', 'email', 'plan', 'approvalStatus', 'storeCount',
];

export interface MerchantListQuery {
  page?: number;
  limit?: number;
  search?: string;
  filter?: MerchantListFilter;
  sort?: MerchantListSort;
  dir?: 'asc' | 'desc';
}

export interface PagedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Per-filter totals. Cannot be derived from a page — see listMerchants. */
export interface MerchantListCounts {
  all: number;
  pending: number;
  active: number;
  suspended: number;
  deleted: number;
}

export interface MerchantListResponse<T> extends PagedResponse<T> {
  counts: MerchantListCounts;
}

/**
 * How long a soft-deleted merchant keeps appearing in the Deleted Merchants
 * list. After this it stops being listed — **the row is not touched**. See
 * docs/18-merchants-stores-merge-plan.md §4 for why this is a query predicate
 * rather than a scheduled purge.
 */
export const DELETED_MERCHANT_RETENTION_DAYS = 365;

export interface RejectMerchantRequest {
  reason: string;
}

export interface UpdateProfileRequest {
  name: string;
  email: string;
}

export interface UpdateThemeModeRequest {
  themeMode: ThemeMode;
}

export interface AuthResponse {
  user: AuthUser;
  accessToken: string;
  /**
   * Rotating refresh token backing a revocable session.
   *
   * Optional so existing clients keep working unchanged while the apps are
   * migrated one at a time; it is only absent where the session store is not
   * wired up (several specs construct AuthService directly).
   */
  refreshToken?: string;
}

export interface AddressSummary {
  id: string;
  label: string;
  recipientName: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone?: string;
  createdAt: string;
}

export interface AddressListResponse {
  items: AddressSummary[];
  total: number;
}

export interface CreateAddressRequest {
  label: string;
  recipientName: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone?: string;
}

export type UpdateAddressRequest = CreateAddressRequest;

export interface WishlistItemSummary {
  id: string;
  product: ProductSummary;
  createdAt: string;
}

export interface WishlistResponse {
  items: WishlistItemSummary[];
  total: number;
}

export interface AddWishlistItemRequest {
  productId: string;
}

// ── Reviews ───────────────────────────────────────────────────────────────
// One unified review shape for all four surfaces the site supports.
// `targetId` is the entity being reviewed — productId for 'product',
// orderId for both 'order' (overall shipping experience) and
// 'delivery_partner' (the delivery executive for that order) — and is
// omitted for 'general' site feedback.
export type ReviewTypeCode = 'product' | 'delivery_partner' | 'order' | 'general';

export interface ReviewPhotoSummary {
  id: string;
  url: string;
  altText?: string;
}

export interface ReviewSummary {
  id: string;
  reviewType: ReviewTypeCode;
  targetId?: string;
  userId: string;
  userName?: string;
  rating: number;
  title?: string;
  comment?: string;
  attributes?: Record<string, unknown>;
  status: string;
  photos: ReviewPhotoSummary[];
  createdAt: string;
  updatedAt: string;
}

export interface ReviewListResponse {
  items: ReviewSummary[];
  total: number;
  averageRating: number;
}

// Whether the current user is allowed to write this review yet — product
// reviews require a delivered order containing that product; order and
// delivery-partner reviews require that specific order to be delivered.
export interface ReviewEligibilityResponse {
  eligible: boolean;
}

// The current user's own reviews for many targets of one review type in a
// single request — e.g. an orders list fetching "my rating" for every
// delivered order at once, instead of one request per order. POST + body
// (not a GET query string) since targetIds can grow arbitrarily long.
export interface ReviewsMineBatchRequest {
  reviewType: ReviewTypeCode;
  targetIds: string[];
}

// Keyed by targetId; a target with no review yet is simply absent from the map.
export type ReviewsMineBatchResponse = Record<string, ReviewSummary>;

export interface CreateReviewRequest {
  reviewType: ReviewTypeCode;
  targetId?: string;
  rating: number;
  title?: string;
  comment?: string;
  attributes?: Record<string, unknown>;
}

export interface UpdateReviewRequest {
  rating?: number;
  title?: string;
  comment?: string;
  attributes?: Record<string, unknown>;
}

export interface AdminListResponse<T> {
  items: T[];
  total: number;
}

export interface BrandSummary {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

export interface CreateBrandRequest {
  name: string;
}

export interface CollectionSummary {
  id: string;
  title: string;
  slug: string;
  description?: string;
  isManual: boolean;
  createdAt: string;
}

export interface CreateCollectionRequest {
  title: string;
  description?: string;
  isManual: boolean;
}

export interface ShippingZoneSummary {
  id: string;
  name: string;
  countries: unknown;
}

export interface ShippingRateSummary {
  id: string;
  zoneId: string;
  name: string;
  amount: number;
  currency: string;
}

export interface CreateShippingRateRequest {
  zoneId: string;
  name: string;
  amount: number;
  currency: string;
}

// ─── GST tax slab profiles ─────────────────────────────────────────────────
export interface TaxSlabProfileSummary {
  id: string;
  name: string;
  code: string;
  hsnGroup?: string;
  igstRateBps: number;
  cgstRateBps: number;
  sgstRateBps: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaxSlabProfileRequest {
  name: string;
  code: string;
  hsnGroup?: string;
  igstRateBps: number;
  cgstRateBps: number;
  sgstRateBps: number;
}

export interface UpdateTaxSlabProfileRequest {
  name?: string;
  hsnGroup?: string;
  igstRateBps?: number;
  cgstRateBps?: number;
  sgstRateBps?: number;
  isActive?: boolean;
}

// ─── Store GST / TCS settings ──────────────────────────────────────────────
export interface GstSettingsResponse {
  gstin?: string;
  defaultTaxInclusive: boolean;
}

export interface UpdateGstSettingsRequest {
  gstin?: string;
  defaultTaxInclusive?: boolean;
}

export interface TcsSettingsResponse {
  tcsRateBps: number;
  tcsThresholdPaise?: number;
}

export interface UpdateTcsSettingsRequest {
  tcsRateBps?: number;
  tcsThresholdPaise?: number;
}

// ─── Tax reports ────────────────────────────────────────────────────────────
export type TaxReportType = 'all' | 'intra' | 'inter';

export interface TaxReportQuery {
  from?: string;
  to?: string;
  type?: TaxReportType;
  /** Filter to orders with at least one line item at this IGST rate (bps). */
  igstRateBps?: number;
  skip?: number;
  take?: number;
}

// One row per order — not per tax slab. An order can mix slabs across its
// line items, so this is the order's own aggregate GST split (frozen at
// checkout time on Order.cgstTotal/sgstTotal/igstTotal), the same shape
// admins already see on the order detail page.
export interface TaxReportTransactionRow {
  orderId: string;
  orderDate: string;
  state: string;
  isInterState: boolean;
  taxableValue: number;
  cgstTotal: number;
  sgstTotal: number;
  igstTotal: number;
  totalTax: number;
  grandTotal: number;
}

export interface TaxReportSummary {
  taxableValue: number;
  cgstTotal: number;
  sgstTotal: number;
  igstTotal: number;
  totalTax: number;
  /** Admin-only accounting figure — see the TCS note on GstTotals above. */
  tcsTotal: number;
}

export interface TaxReportResponse {
  items: TaxReportTransactionRow[];
  total: number;
  summary: TaxReportSummary;
}

export type DiscountCodeType = 'fixed' | 'percentage' | 'free_shipping';
export type DiscountCodeEligibility = 'all' | 'new_customers';
export type DiscountCodeInvalidReason = 'taken' | 'invalid-format';

export interface DiscountCodeSummary {
  id: string;
  code: string;
  type: DiscountCodeType | string;
  value: number;
  minOrderAmount?: number;
  maxDiscountAmount?: number;
  customerEligibility: DiscountCodeEligibility | string;
  stackable: boolean;
  usageLimit?: number;
  usedCount: number;
  startsOn?: string;
  endsOn?: string;
  isActive: boolean;
  createdAt: string;
}

export interface CreateDiscountCodeRequest {
  code: string;
  type: DiscountCodeType;
  value: number;
  minOrderAmount?: number;
  maxDiscountAmount?: number;
  customerEligibility?: DiscountCodeEligibility;
  stackable?: boolean;
  usageLimit?: number;
  startsOn?: string;
  endsOn?: string;
  isActive?: boolean;
}

export type UpdateDiscountCodeRequest = Partial<CreateDiscountCodeRequest>;

export interface DiscountCodeAvailability {
  available: boolean;
  reason?: DiscountCodeInvalidReason;
}

/** One changed field, already diffed server-side. */
export interface ChangeFieldDiff {
  field: string;
  from?: unknown;
  to?: unknown;
}

/**
 * One changed database ROW — not one changed column. A 7-field product edit is
 * a single ChangeLogSummary whose `diff` has 7 entries.
 */
export interface ChangeLogSummary {
  id: string;
  schemaName: string;
  tableName: string;
  recordId: string;
  operation: string;
  changedColumns?: string[];
  oldValue?: Record<string, unknown> | null;
  newValue?: Record<string, unknown> | null;
  diff?: ChangeFieldDiff[];
  changedBy?: string;
  actorName?: string;
  actorRole?: string;
  ipAddress?: string;
  correlationId?: string;
  batchId?: string;
  /** Postgres bigint, serialised as a string — JSON cannot carry a BigInt. */
  txId?: string | null;
  source?: string;
  changedOn: string;
}

/**
 * One user action, grouping every row it changed across tables. "Save product"
 * is one batch covering the product, its variants and its images.
 */
export interface ChangeBatchSummary {
  batchId: string;
  storeId?: string;
  actorId?: string;
  actorRole?: string;
  actorName?: string;
  ipAddress?: string;
  correlationId?: string;
  action?: string;
  summary?: string;
  route?: string;
  method?: string;
  source: string;
  rowCount: number;
  tableCount: number;
  startedAt: string;
  endedAt?: string;
}

export interface ChangeBatchDetail {
  batch: ChangeBatchSummary | null;
  changes: ChangeLogSummary[];
}

export interface InterfaceLogSummary {
  id: string;
  direction: string;
  method: string;
  path: string;
  routePattern?: string;
  statusCode: number;
  durationMs?: number;
  userId?: string;
  ipAddress?: string;
  correlationId?: string;
  batchId?: string;
  truncated?: boolean;
  sampleRate?: number;
  requestHeaders?: Record<string, unknown> | null;
  requestPayload?: unknown;
  responsePayload?: unknown;
  errorPayload?: unknown;
  requestedOn: string;
  respondedOn?: string;
}

export interface ActivityLogSummary {
  id: string;
  userId?: string;
  anonymousId?: string;
  sessionId?: string;
  app: string;
  eventName: string;
  route?: string;
  targetId?: string;
  props?: Record<string, string | number | boolean> | null;
  occurredAt: string;
  receivedAt: string;
  referrer?: string;
}

/** Everything one request touched — the view the correlation id exists for. */
export interface AuditCorrelationView {
  correlationId: string;
  inbound: InterfaceLogSummary[];
  outbound: InterfaceLogSummary[];
  changes: ChangeLogSummary[];
  batches: ChangeBatchSummary[];
}

/** Filter options drawn from the data that actually exists, not a hardcoded list. */
export interface AuditFacets {
  tables: { schemaName: string; tableName: string; count: number }[];
  actions: { action: string | null; count: number }[];
  actors: { actorId: string | null; actorName: string | null; count: number }[];
  events: { eventName: string; count: number }[];
  allowedEvents: string[];
}

export interface AuditStats {
  batches: number;
  changes: number;
  calls: number;
  failures: number;
  activity: number;
  windowHours: number;
}

export interface AuditStorageRow {
  table: string;
  bytes: number;
  rows: number;
}

// ── Merchant analytics ─────────────────────────────────────────────────────

export interface AnalyticsFunnelStep {
  event: string;
  label: string;
  /** Sessions, not events — an event-based funnel reports conversion above 100%. */
  sessions: number;
  shareOfTop: number;
  dropFromPrevious: number;
}

export interface AnalyticsFunnel {
  days: number;
  steps: AnalyticsFunnelStep[];
  conversionRate: number;
  abandonedCheckouts: number;
  abandonmentRate: number;
}

/** What to do about a product, not just how it performed. */
export type ProductSignal =
  | 'selling' | 'not-converting' | 'out-of-stock' | 'not-found' | 'low-traffic';

export interface AnalyticsProductRow {
  productId: string;
  title: string;
  views: number;
  addToCarts: number;
  purchases: number;
  /** Paise, taken from the order table so it reconciles with the books. */
  revenue: number;
  inStock: number | null;
  conversionRate: number;
  signal: ProductSignal;
}

export interface AnalyticsSearchRow {
  term: string;
  searchCount: number;
  zeroResults: number;
  unmetDemand: boolean;
}

export interface AnalyticsTrendPoint {
  day: string;
  sessions: number;
  orders: number;
  revenue: number;
}

export interface AnalyticsSummary {
  days: number;
  sessions: number;
  orders: number;
  revenue: number;
  averageOrderValue: number;
  conversionRate: number;
  abandonmentRate: number;
}

// ── Platform analytics ─────────────────────────────────────────────────────

export interface PlatformAnalyticsOverview {
  days: number;
  gmv: number;
  orders: number;
  totalStores: number;
  activeStores: number;
  sellingStores: number;
  sellingShare: number;
}

export interface PlatformActivationStep {
  key: string;
  label: string;
  stores: number;
  share: number;
}

export interface PlatformChurnRow {
  storeId: string;
  name: string | null;
  lastOrderAt: string | null;
  idleDays: number;
  lifetimeOrders: number;
  lifetimeGmv: number;
}

export interface PlatformCostRow {
  storeId: string;
  name: string | null;
  apiCalls: number;
  apiErrors: number;
  auditRows: number;
  sessions: number;
  orders: number;
  gmv: number;
  storageBytes: number;
  callsPerOrder: number | null;
  revenuePerCall: number;
}

export interface PlatformReliabilityRow {
  storeId: string;
  name: string | null;
  apiCalls: number;
  apiErrors: number;
  errorRate: number;
}

// ── Merchant activity (store-facing audit) ─────────────────────────────────

export type MerchantAuditCategory =
  | 'Catalog' | 'Pricing' | 'Orders' | 'Stock' | 'Team' | 'Settings';

/** One field a merchant changed, already labelled and formatted for reading. */
export interface MerchantFieldChange {
  field: string;
  label: string;
  /** null for a newly created record — there was no previous value. */
  from: string | null;
  to: string | null;
}

export interface MerchantAuditRecord {
  id: string;
  category: MerchantAuditCategory;
  entityLabel: string;
  entityName: string;
  operation: string;
  title: string;
  changes: MerchantFieldChange[];
}

/** One user action, with every record it touched. */
export interface MerchantActivityEntry {
  batchId: string;
  occurredAt: string;
  actorName: string;
  actorRole: string | null;
  actorId: string | null;
  isSystem: boolean;
  category: MerchantAuditCategory | null;
  title: string;
  recordCount: number;
  records: MerchantAuditRecord[];
}

export interface MerchantAuditHistoryEntry extends MerchantAuditRecord {
  occurredAt: string;
  actorName: string;
  actorRole: string | null;
  batchId: string | null;
}

export interface MerchantAuditActor {
  actorId: string | null;
  actorName: string | null;
  count: number;
}

export interface MerchantAuditSummary {
  actions: number;
  people: number;
  windowDays: number;
  categories: MerchantAuditCategory[];
}

/** Keyset paging: pass the previous page's `nextCursor`, not a page number. */
export interface MerchantActivityPage {
  items: MerchantActivityEntry[];
  hasMore: boolean;
  nextCursor: string | null;
}

export interface MerchantAuditQueryParams {
  take?: number;
  skip?: number;
  /** Previous page's `nextCursor`. Cheaper than skip at depth, and needs no COUNT. */
  cursor?: string;
  search?: string;
  from?: string;
  to?: string;
  category?: string;
  actorId?: string;
  schemaName?: string;
  tableName?: string;
  recordId?: string;
}

/** Server-side filter parameters. Every one of these is applied in SQL. */
export interface AuditQueryParams {
  take?: number;
  skip?: number;
  search?: string;
  from?: string;
  to?: string;
  // changes
  schemaName?: string;
  tableName?: string;
  recordId?: string;
  operation?: string;
  changedBy?: string;
  batchId?: string;
  action?: string;
  actorId?: string;
  // interface
  method?: string;
  direction?: string;
  path?: string;
  statusRange?: string;
  statusCode?: string;
  correlationId?: string;
  userId?: string;
  minDurationMs?: string;
  // activity
  eventName?: string;
  sessionId?: string;
  anonymousId?: string;
  app?: string;
}

// ── Payment Methods ────────────────────────────────────────────────────────

export interface PaymentMethodSummary {
  id: string;
  label: string;
  type: 'card' | 'bank';
  brand?: string;
  last4: string;
  expMonth?: number;
  expYear?: number;
  bankName?: string;
  isDefault: boolean;
  createdAt: string;
}

export interface PaymentMethodListResponse {
  items: PaymentMethodSummary[];
  total: number;
}

export interface AddPaymentMethodRequest {
  label: string;
  type: 'card' | 'bank';
  last4: string;
  brand?: string;
  expMonth?: number;
  expYear?: number;
  bankName?: string;
  token?: string;
  makeDefault?: boolean;
}

// ── Theme Library ──────────────────────────────────────────────────────────

export interface ThemeSettings {
  primaryColor: string;
  primaryHover: string;
  accentColor: string;
  surfaceColor: string;
  bgColor: string;
  textColor: string;
  textMuted: string;
  borderColor: string;
  successColor: string;
  dangerColor: string;
  fontFamily: string;
  fontUrl?: string;
  // Optional on purpose: existing theme rows carry neither key, and a template
  // that wants a distinct display face falls back to fontFamily/fontUrl when
  // they are absent. Keeping them optional is what avoids a row migration and
  // a change to the admin theme editor.
  headingFontFamily?: string;
  headingFontUrl?: string;
  borderRadiusSm: string;
  borderRadiusMd: string;
  borderRadiusLg: string;
}

export interface ThemeSummary {
  id: string;
  name: string;
  slug: string;
  description?: string;
  previewImageUrl?: string;
  settings: ThemeSettings;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ThemeListResponse {
  items: ThemeSummary[];
  total: number;
}

export interface CreateThemeRequest {
  name: string;
  description?: string;
  previewImageUrl?: string;
  settings: ThemeSettings;
}

export interface UpdateThemeRequest {
  name?: string;
  description?: string;
  previewImageUrl?: string;
  settings?: Partial<ThemeSettings>;
}

// ── Storefront Templates ───────────────────────────────────────────────────
//
// The *structure* axis of a storefront: which sections a page renders and in
// what order. Composes with Theme above, which owns colours and fonts only —
// any theme must be applicable to any template.
//
// NOT the email template system (`TemplateSummary`, `TemplateStatus`, … below).
// Everything here is prefixed `StorefrontTemplate` precisely to keep the two
// apart at every call site.

/**
 * Minimum `Organization.plan` required to apply a template. Ordered
 * free < basic < premium; the authoritative comparison lives in
 * `StorefrontTemplatesService.applyToStore`, not in the client.
 */
export type StorefrontTemplateTier = 'free' | 'basic' | 'premium';

/**
 * Display-only metadata for the merchant picker. It deliberately says nothing
 * about which pages the template overrides — the code registry in `web/` is
 * the single source of truth for layout.
 */
export interface StorefrontTemplateMetadata {
  features?: string[];
  badge?: string;
  [key: string]: unknown;
}

export interface StorefrontTemplateSummary {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  previewImageUrl?: string | null;
  tier: StorefrontTemplateTier;
  metadata?: StorefrontTemplateMetadata | null;
  isPublished: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  /**
   * Set only by the merchant-facing list: true when `tier` outranks the
   * organisation's plan. Locked templates are still returned so the picker can
   * upsell rather than hide them — this flag drives the "Upgrade to unlock"
   * affordance and is UX, never the gate.
   */
  isLocked?: boolean;
}

export interface StorefrontTemplateListResponse {
  items: StorefrontTemplateSummary[];
  total: number;
}

export interface CreateStorefrontTemplateRequest {
  name: string;
  description?: string;
  previewImageUrl?: string;
  tier?: StorefrontTemplateTier;
  metadata?: StorefrontTemplateMetadata;
}

export interface UpdateStorefrontTemplateRequest {
  name?: string;
  description?: string;
  previewImageUrl?: string;
  tier?: StorefrontTemplateTier;
  metadata?: StorefrontTemplateMetadata;
}

export interface ApplyStorefrontTemplateRequest {
  storefrontTemplateId: string;
}

/**
 * Returned by GET /store/config — the storefront's single boot round trip,
 * folding /store/info and /store/theme together because this now sits on the
 * SSR critical path.
 *
 * `template` is never an error path: an unpublished, inactive, deleted or
 * unset template resolves to `classic`, and is null only if `classic` itself
 * is missing from the database (unseeded), where the client falls back to the
 * `classic` code registry entry anyway.
 */
/** A social profile, ready to render as a footer icon link. */
export interface StorefrontSocialLink {
  /** Stable key the footer maps to an icon: twitter, facebook, instagram… */
  network: 'twitter' | 'facebook' | 'instagram' | 'linkedin' | 'youtube' | 'whatsapp';
  label: string;
  url: string;
}

/**
 * Everything the storefront needs to describe itself to a search engine and to
 * a visitor, resolved through the store → merchant fallback.
 *
 * Delivered inside the boot round trip rather than as its own request: SEO tags
 * have to be in the server-rendered HTML to be worth anything, and a second
 * call would put a network hop on the critical path for something that must be
 * present before first paint.
 *
 * Everything here is public by nature — it is emitted into the page for
 * crawlers to read. Analytics and verification IDs included: they end up in
 * `<head>` either way, so treating them as secrets would be a false comfort.
 */
export interface StorefrontBranding {
  metaTitle: string | null;
  metaDescription: string | null;
  metaKeywords: string | null;
  /** Absolute base for canonical URLs, when the merchant pinned one. */
  canonicalBaseUrl: string | null;
  robotsPolicy: string;

  logoUrl: string | null;
  faviconUrl: string | null;
  ogImageUrl: string | null;

  /** The town this shop serves — what "grocery store in Karimnagar" matches. */
  areaName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  whatsappUrl: string | null;
  mapsUrl: string | null;
  social: StorefrontSocialLink[];

  /** Applied to product page titles: {product}, {store}, {area}. */
  productTitleTemplate: string | null;

  googleSiteVerification: string | null;
  bingSiteVerification: string | null;
  gaMeasurementId: string | null;
  gtmContainerId: string | null;

  /**
   * Address and hours for the footer, already resolved. Null when the address
   * is too partial to show — a street with no town is a wrong answer, not a
   * smaller one.
   */
  address: {
    streetAddress: string;
    locality: string;
    region: string | null;
    postalCode: string;
    countryCode: string;
  } | null;
  openingHours: { day: string; opens: string; closes: string }[];

  /**
   * Question-and-answer pairs, merchant-written merged with generated.
   *
   * Rendered as visible text AND as `FAQPage` markup. The visible half is the
   * one AI search engines quote; the markup only tells them which business the
   * answers belong to.
   */
  faqs: { question: string; answer: string; custom?: boolean }[];

  /** False when this merchant has opted out of AI crawlers in robots.txt. */
  allowAiCrawlers: boolean;

  /** Days to return an item. Null means the merchant has not stated one. */
  returnWindowDays: number | null;
  /**
   * Free-delivery threshold in paise, derived from the lowest active shipping
   * channel threshold rather than stored — see doc 25 §8.
   */
  freeDeliveryOverPaise: number | null;

  /** When this store's branding last changed, as a freshness signal. */
  updatedAt: string | null;

  /**
   * The raw resolved record, carried so the storefront can build its
   * `Organization` + `LocalBusiness` markup with the *public* origin — which
   * only the rendering process knows, since the API sits behind a proxy and its
   * own host is an internal address.
   *
   * Shaped as `ResolvedBranding` from `@cart/shared-utils` and passed straight
   * to the shared builder, so there is exactly one implementation of this
   * markup rather than one per app.
   */
  jsonLdInput: Record<string, unknown>;
}

export interface StoreConfigResponse {
  store: StoreInfoResponse;
  theme: ThemeSummary | null;
  template: StorefrontTemplateSummary | null;
  /**
   * Branding and SEO. Null only when the store has no branding row and no
   * merchant defaults — the storefront renders exactly as it did before.
   */
  branding: StorefrontBranding | null;
  /**
   * True when the store's organisation no longer holds a plan covering the
   * applied template's tier (downgrade, or `planExpiresAt` in the past).
   *
   * Deliberately does NOT change what is rendered — the template is still
   * returned as-is. A live storefront never silently changes appearance;
   * this flag exists to raise a banner in admin and let billing decide.
   */
  entitlementLapsed: boolean;
}

// ─── Email templates ────────────────────────────────────────────────────────
/**
 * `system_default` means the store has no override and is using the platform's
 * default — a working state, not a missing one. `not_configured` now only means
 * the type has no default either, which is a real gap.
 */
export type TemplateStatus = 'draft' | 'active' | 'disabled' | 'not_configured' | 'system_default';

export interface TemplateSummary {
  typeId: string;
  typeCode: string;
  typeName: string;
  category: string;
  description?: string;
  typeIsActive: boolean;
  templateId?: string;
  name?: string;
  subject?: string;
  bodyHtml?: string;
  isActive?: boolean;
  isPublished?: boolean;
  status: TemplateStatus;
  versionCount: number;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface TemplateListResponse {
  items: TemplateSummary[];
  total: number;
}

export interface UpsertTemplateRequest {
  name: string;
  subject: string;
  bodyHtml: string;
  isActive?: boolean;
  publish: boolean;
}

export interface SetTemplateStatusRequest {
  isActive: boolean;
}

export interface TemplateVersionSummary {
  id: string;
  versionNumber: number;
  name: string;
  subject: string;
  bodyHtml: string;
  isActive: boolean;
  isPublished: boolean;
  action: string;
  comment?: string;
  createdBy: string;
  createdAt: string;
}

/**
 * The platform's own current version of a template.
 *
 * Shown beside a store's history so a merchant can see that a newer platform
 * template exists at all. Without it, a store carrying its own copy silently
 * misses every later improvement — which is exactly how stores ended up sending
 * delivery emails with no invoice link in them.
 */
export interface SystemTemplateVersion {
  name: string;
  subject: string;
  bodyHtml: string;
  /** When the platform last changed it. */
  updatedAt: string;
  /** True when this store is already sending exactly this content. */
  matchesCurrent: boolean;
  /** False when the store has no copy and therefore tracks the platform automatically. */
  hasOverride: boolean;
}

export interface TemplateVersionListResponse {
  items: TemplateVersionSummary[];
  total: number;
  /** Absent only for a type the platform ships no default for. */
  systemDefault?: SystemTemplateVersion;
}

export interface SendTestEmailRequest {
  email: string;
  subject?: string;
  bodyHtml?: string;
}

export interface EmailSettingsResponse {
  senderEmail?: string;
  senderName?: string;
  replyToEmail?: string;
  logoUrl?: string;
  primaryColor?: string;
  providerLabel: string;
  updatedAt?: string;
}

export interface UpdateEmailSettingsRequest {
  senderEmail?: string;
  senderName?: string;
  replyToEmail?: string;
  logoUrl?: string;
  primaryColor?: string;
}

export type EmailLogStatus = 'sent' | 'failed';

export interface EmailLogSummary {
  id: string;
  typeCode?: string;
  typeName?: string;
  recipient: string;
  subject: string;
  status: EmailLogStatus;
  errorMessage?: string;
  createdAt: string;
}

export interface EmailLogListResponse {
  items: EmailLogSummary[];
  total: number;
}

export interface EmailLogQuery {
  status?: EmailLogStatus;
  typeCode?: string;
  limit?: number;
}

export interface ApplyThemeRequest {
  themeId: string;
}

/** A store the signed-in user may file a new product into. */
export interface CreatableStoreOption {
  id: string;
  name: string;
  city: string | null;
  isActive: boolean;
}
